/** Angular imports */
import { Component, computed, inject, signal } from '@angular/core';
import { catchError, EMPTY } from 'rxjs';

/**  Core imports */
import { MediaLibraryService } from '@core';

/** Component imports */
import { PlaylistSettingsContentComponent } from '@features/playlists/components/ui/playlist-settings-content/playlist-settings-content.component';
import { PlaylistSettingsData } from '@features/playlists/interfaces';
import { CreatePlaylistStepsService } from '@features/playlists/services/steps/steps.service';
import { PlaylistGridComponent } from '@shared';

/** Local imports */
import { ApiContent } from '@core/services/media-library/media-library.interface';
import { UiThumbnailContent } from '@features/media-library/interfaces';
import { Logger, withLoading } from '@shared/utils';

@Component({
    selector: 'app-settings',
    imports: [PlaylistGridComponent, PlaylistSettingsContentComponent],
    templateUrl: './settings.component.html',
    styleUrl: './settings.component.scss',
})
export class SettingsComponent {
    /** Service in managing media contents and API operations */
    private mediaLibraryService = inject(MediaLibraryService);

    /** Service in managing the create playlist flow and draft data */
    private stepsService = inject(CreatePlaylistStepsService);

    protected isLoadingContents = signal<boolean>(false);

    protected settingsDraft = computed(() => this.stepsService.playlistDraft().settings);

    protected playlistContents = computed(() => {
        return this.stepsService.playlistDraft().selectAssets.addedPlaylistContents;
    });

    /**
     * Removes a content item from the playlist draft
     * @param event The emitted removal event containing item metadata
     */
    protected removeContent(event: { type: string; data: UiThumbnailContent }): void {
        const { type, data } = event;

        if (type === 'content') {
            const selectAssetsDraft = this.stepsService.playlistDraft().selectAssets;
            const updatedContents = selectAssetsDraft.addedPlaylistContents.filter(
                (c) => c.contentId !== data.contentId,
            );

            this.stepsService.updateDraft('selectAssets', {
                ...selectAssetsDraft,
                addedPlaylistContents: updatedContents,
                selectedContents: updatedContents, // Sync both arrays
            });
        }
    }

    /**
     * Handles reordering of contents in the playlist grid
     * @param updatedContents The newly ordered list of contents
     */
    protected onReorder(updatedContents: UiThumbnailContent[]): void {
        const selectAssetsDraft = this.stepsService.playlistDraft().selectAssets;
        this.stepsService.updateDraft('selectAssets', {
            ...selectAssetsDraft,
            addedPlaylistContents: updatedContents,
            selectedContents: updatedContents, // Sync both arrays
        });
    }

    /**
     * Maps API response to UI requirements
     */
    private mapDataToUi(data: ApiContent[]): void {}

    /** ============================= API Calls ================================= */

    /** Load paginate media library contents */
    private loadContents(): void {
        this.mediaLibraryService
            .getContents()
            .pipe(
                withLoading(this.isLoadingContents),
                catchError((err) => {
                    Logger.error('CreatePlaylistSettings', 'error loading contents.', err);
                    return EMPTY;
                }),
            )
            .subscribe({
                next: (res) => {
                    console.log('data', res.data);
                    this.mapDataToUi(res.data);
                },
            });
    }

    protected onSettingsChange(data: PlaylistSettingsData): void {
        console.log('CreatePlaylistSettings received data:', data);
        this.stepsService.updateDraft('settings', data);
    }
}
